import { Link } from 'react-router-dom';
import { Zap, MousePointer, SplitSquareHorizontal } from 'lucide-react';

export default function BlogBetMimicVsManual() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <header className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-600/10 border border-accent-500/40 text-xs text-accent-100 uppercase tracking-wide">
            <SplitSquareHorizontal className="w-3 h-3" />
            <span>Manual vs Copy Trading</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            When to Manually Trade Polymarket vs Let BetMimic Copy for You
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            BetMimic gives you both: manual trading tools wired into the Polymarket CLOB, and a full
            copy‑trading engine that mirrors other wallets. Knowing when to press buttons yourself and
            when to let the engine run is where a lot of the edge lives.
          </p>
        </header>

        {/* Section: Strengths of manual trading */}
        <section className="card space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-primary-400" />
            Where manual Polymarket trading shines
          </h2>
          <p className="text-sm text-dark-300">
            Manual trading is still king when the whole point is expressing <span className="font-medium">your</span>{' '}
            view. If you&apos;ve done the work on a specific market, you probably want direct control over
            entries, hedges and exits.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>One‑off event trades where you have unique information or timing.</li>
            <li>Active hedging around positions that BetMimic copied for you automatically.</li>
            <li>Scaling in and out of big conviction trades where you want custom sizing.</li>
          </ul>
          <p className="text-xs text-dark-500">
            BetMimic exposes manual order book, market/limit orders and close‑position tools directly
            into your wallet so you can still operate like a normal Polymarket degenerate when it
            matters.
          </p>
        </section>

        {/* Section: Where copy trading is strictly better */}
        <section className="card space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            Where copy trading with BetMimic is strictly better
          </h2>
          <p className="text-sm text-dark-300">
            Copy trading shines where markets are fast, noisy and research heavy. Instead of chasing
            screenshots or refreshing the order book, you let someone else do the grind while your wallet
            follows their fills.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              High‑frequency sports and short‑dated event markets where timing is everything.
            </li>
            <li>
              Traders who live in specific niches – US politics, crypto governance, meme events – and
              consistently size well.
            </li>
            <li>
              Times when you want to be in the flow but cannot sit in front of Polymarket all day.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            BetMimic&apos;s engine keeps these copies in sync with Polymarket&apos;s own positions API, so your
            dashboard stays a real representation of what the chain thinks you own.
          </p>
        </section>

        {/* Section: Running both side by side */}
        <section className="card space-y-3">
          <h2 className="text-xl font-semibold">Running manual and copy trading side by side</h2>
          <p className="text-sm text-dark-300">
            The most powerful setup for a lot of users is running a small manual book and a structured
            copy book from the same wallet.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              Use copy configs for the background noise – constant small tickets following your favorite
              traders across Polymarket.
            </li>
            <li>
              Use manual trading tools when you have a specific thesis or want to override a copied
              position.
            </li>
            <li>
              Let BetMimic&apos;s reconciliation and auto‑redeem logic keep the combined PnL clean.
            </li>
          </ul>
        </section>

        {/* CTA */}
        <section className="space-y-3">
          <p className="text-sm text-dark-300">
            You don&apos;t have to choose between being a discretionary Polymarket trader and a copy trader.
            BetMimic is built so both styles can run on the same wallet with one dashboard keeping score.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/positions"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
            >
              <Zap className="w-4 h-4" />
              See how manual and copied trades show up in PnL
            </Link>
            <Link
              to="/copy-configs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              Configure which Polymarket traders to copy
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
